import React, { useState, useRef, useEffect, useCallback } from "react";
import { useSheetContext } from "../contexts/SheetContext";
import { useSessionContext } from "../contexts/SessionContext";
import { useInitiativeContext } from "../contexts/InitiativeContext";
import { useActorContext } from "../contexts/ActorContext";
import { useTokenContext } from "../contexts/TokenContext";
import { getInitials } from "../utils";
import PortraitMedia from "./PortraitMedia";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "./ui/tooltip";
import {
  ContextMenu,
  ContextMenuCheckboxItem,
  ContextMenuContent,
  ContextMenuLabel,
  ContextMenuRadioGroup,
  ContextMenuRadioItem,
  ContextMenuSeparator,
  ContextMenuTrigger,
} from "./ui/context-menu";
import { EyeOff, FileX } from "lucide-react";
import { useI18n } from "../i18n";

type PortraitVisibility = 'always' | 'gm-only' | 'hidden';

const ACCENT_COLORS = [
  "#6366f1", // indigo
  "#f59e0b", // amber
  "#10b981", // emerald
  "#ef4444", // red
  "#8b5cf6", // violet
  "#06b6d4", // cyan
  "#f97316", // orange
  "#ec4899", // pink
];

const COLOR_STORAGE_KEY = "participant-color";

const ORDER_STORAGE_PREFIX = "roll1:portraits-order:";

/** Exported: CharacterPanel shows/cycles the card visibility from the
 *  character list too (visibility consolidation, 2026-07-08). */
export function getEffectiveVisibility(
  sheet: { sheetType?: string; values: Record<string, unknown> },
): PortraitVisibility {
  const raw = sheet.values['portrait_visibility'];
  if (raw === 'always' || raw === 'gm-only' || raw === 'hidden') return raw;
  return sheet.sheetType === 'pc' ? 'always' : 'gm-only';
}
export type { PortraitVisibility as CardVisibility };

export type AvatarSize = 'small' | 'medium' | 'large';

export const AVATAR_SIZES: Record<AvatarSize, { width: number; height: number }> = {
  small: { width: 96, height: 128 },
  medium: { width: 144, height: 192 },
  large: { width: 192, height: 256 },
};

interface CharacterPortraitsProps {
  onOpenSheet?: (id: string) => void;
  avatarSize?: AvatarSize;
  onAvatarSizeChange?: (size: AvatarSize) => void;
}

function getHpFillClass(hp: number, maxHp: number): string {
  if (maxHp <= 0) return '';
  const pct = hp / maxHp;
  if (pct <= 0.25) return 'character-portraits__hp-fill--critical';
  if (pct <= 0.5) return 'character-portraits__hp-fill--low';
  return '';
}

const CharacterPortraits = React.memo(function CharacterPortraits({
  onOpenSheet,
  avatarSize,
  onAvatarSizeChange,
}: CharacterPortraitsProps) {
  const { t } = useI18n();
  const size = AVATAR_SIZES[avatarSize ?? 'medium'];
  const { sheets: charSheets } = useSheetContext();
  // isGM — канонический признак роли из SessionContext (как во всём проекте);
  // mp.participant.role может отсутствовать в моках/ранней фазе подключения.
  const { mp, isGM: isGm } = useSessionContext();
  const { initiative } = useInitiativeContext();
  const { actorSheetId, canActAs, setActor } = useActorContext();
  const campaignId = mp.session?.id ?? null;

  // Scene-scoping (2026-07-08, по выбору владельца): PC-карточки видны всегда,
  // NPC — только когда его токен стоит на АКТИВНОЙ странице. Смена карты сама
  // убирает NPC прошлой сцены из ленты — ручное жонглирование gm-only при
  // переходах между локациями больше не нужно. Для игроков скрытый токен
  // (token.visible=false) сцену не открывает. Возврат NPC без токена — через
  // список персонажей (кнопка-карточка) либо выставлением токена.
  const { tokens: tokensHook } = useTokenContext();
  const sceneSheetIds = new Set(
    tokensHook.tokens
      .filter((tk: { characterSheetId?: string | null; visible?: boolean }) =>
        tk.characterSheetId && (isGm || tk.visible !== false))
      .map((tk: { characterSheetId?: string | null }) => tk.characterSheetId as string),
  );

  const visibleSheets = charSheets.sheets.filter((s) => {
    const eff = getEffectiveVisibility(s);
    const onScene = s.sheetType === 'pc' || sceneSheetIds.has(s.id);
    if (isGm) {
      // GM видит все карточки сцены, включая hidden (сильно затемнённые +
      // бейдж): скрыв карточку из ленты полностью, GM терял способ вернуть
      // её обратно (прод-инцидент 9FWGR6) — теперь возврат есть и из списка
      // персонажей, но карточки текущей сцены всё равно держим досягаемыми.
      return (s.sheetType === 'pc' || s.sheetType === 'npc') && onScene;
    }
    // Players only see effective 'always' AND on-scene
    return eff === 'always' && onScene;
  });

  // Local display order (persisted per campaign in localStorage)
  const [order, setOrder] = useState<string[]>(() =>
    visibleSheets.map((s) => s.id),
  );

  const mergeOrder = useCallback(
    (prev: string[], ids: string[], stored?: string[]): string[] => {
      const incoming = new Set(ids);
      const base = stored ?? prev;
      const known = base.filter((id) => incoming.has(id));
      const knownSet = new Set(known);
      // Unknown ids go to the end.
      const rest = ids.filter((id) => !knownSet.has(id));
      return [...known, ...rest];
    },
    [],
  );

  // Restore persisted order on mount / when campaign changes.
  useEffect(() => {
    let stored: string[] | undefined;
    if (campaignId) {
      try {
        const raw = localStorage.getItem(ORDER_STORAGE_PREFIX + campaignId);
        if (raw) {
          const parsed: unknown = JSON.parse(raw);
          if (Array.isArray(parsed)) {
            stored = parsed.filter((v): v is string => typeof v === 'string');
          }
        }
      } catch {}
    }
    setOrder((prev) =>
      mergeOrder(prev, visibleSheets.map((s) => s.id), stored),
    );
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [charSheets.sheets, campaignId]);

  const persistOrder = useCallback(
    (next: string[]) => {
      if (!campaignId) return;
      try {
        // Merge with the stored order instead of overwriting: with scene
        // scoping `next` holds only the CURRENT page's cards — a plain write
        // wiped the saved positions of NPCs from every other scene.
        let merged = next;
        try {
          const raw = localStorage.getItem(ORDER_STORAGE_PREFIX + campaignId);
          const stored: unknown = raw ? JSON.parse(raw) : null;
          if (Array.isArray(stored)) {
            const nextSet = new Set(next);
            merged = [...next, ...stored.filter((id): id is string => typeof id === 'string' && !nextSet.has(id))];
          }
        } catch {}
        localStorage.setItem(
          ORDER_STORAGE_PREFIX + campaignId,
          JSON.stringify(merged),
        );
      } catch {}
    },
    [campaignId],
  );

  const setVisibility = useCallback(
    (sheetId: string, visibility: PortraitVisibility) => {
      try {
        mp.send({ type: 'sheet:set-portrait-visibility', sheetId, visibility } as any);
      } catch {}
    },
    [mp],
  );

  // Own color — local state, persisted in localStorage, synced via WS
  const [selfColor, setSelfColor] = useState<string>(() => {
    try { return localStorage.getItem(COLOR_STORAGE_KEY) ?? '#6366f1'; } catch { return '#6366f1'; }
  });

  const handleColorChange = useCallback((hex: string) => {
    setSelfColor(hex);
    try { localStorage.setItem(COLOR_STORAGE_KEY, hex); } catch {}
    try { mp.send({ type: 'participant:set-color', color: hex } as any); } catch {}
  }, [mp]);

  const dragId = useRef<string | null>(null);
  const [dragOverId, setDragOverId] = useState<string | null>(null);

  if (visibleSheets.length === 0) return null;

  const participants = mp.participants;
  const selfName = mp.participant?.name ?? null;
  const activeName = initiative.currentIndex >= 0
    ? initiative.entries[initiative.currentIndex]?.name
    : null;

  const sheetMap = Object.fromEntries(visibleSheets.map((s) => [s.id, s]));
  // Order-known cards first, then any visible sheet the local order hasn't
  // learned yet. Rendering strictly from `order` dropped NPC cards of a NEW
  // scene until something else touched the sheet list — the order-sync effect
  // only fires on sheet-list changes, but scene scoping changes visibleSheets
  // on PAGE changes (tokens), which that effect never sees.
  const orderKnown = new Set(order);
  const sorted = [
    ...order.map((id) => sheetMap[id]).filter(Boolean),
    ...visibleSheets.filter((s) => !orderKnown.has(s.id)),
  ];

  return (
    <TooltipProvider delayDuration={200}>
      <div className="character-portraits">
        <div
          className="character-portraits__track"
          onContextMenu={(e) => {
            e.preventDefault();
            if (onAvatarSizeChange) {
              const sizes: AvatarSize[] = ['small', 'medium', 'large'];
              const idx = sizes.indexOf(avatarSize ?? 'medium');
              onAvatarSizeChange(sizes[(idx + 1) % 3]);
            }
          }}
        >
          {sorted.map((sheet, index) => {
            const owner = participants.find((p) => p.name === sheet.ownerName);
            const isOnline = owner?.online ?? false;
            const isSelf = sheet.ownerName === selfName;
            // Own color: use local state for instant feedback; others: from WS participant data
            const accentColor = isSelf
              ? selfColor
              : (owner?.color ?? ACCENT_COLORS[index % ACCENT_COLORS.length]);
            const isActiveTurn = activeName === sheet.name;
            const isDragging = dragId.current === sheet.id;
            const isDragOver = dragOverId === sheet.id && dragOverId !== dragId.current;

            const hp = Number(sheet.values['hp']) || 0;
            const maxHp = Number(sheet.values['max_hp']) || 0;
            const hpPct = maxHp > 0 ? Math.max(0, Math.min(100, (hp / maxHp) * 100)) : 0;
            const showHpBar = maxHp > 0;

            const effVisibility = getEffectiveVisibility(sheet);
            const isActor = sheet.id === actorSheetId;

            let className = 'character-portraits__item';
            if (isActiveTurn) className += ' character-portraits__item--active-turn';
            if (isDragging)   className += ' character-portraits__item--dragging';
            if (isDragOver)   className += ' character-portraits__item--drag-over';
            // NB: класс должен совпадать с CSS `--current-actor` (index.css) —
            // первая версия вешала `--actor`, и рамка актора не применялась.
            if (isActor)      className += ' character-portraits__item--current-actor';
            if (isGm && effVisibility === 'gm-only') {
              className += ' character-portraits__item--gm-only';
            }
            if (isGm && effVisibility === 'hidden') {
              className += ' character-portraits__item--hidden-card';
            }

            const handleClick = () => {
              if (canActAs(sheet.id)) setActor(sheet.id);
              else onOpenSheet?.(sheet.id);
            };

            const portraitButton = (
              <button
                    className={className}
                    draggable
                    onClick={handleClick}
                    onDoubleClick={() => onOpenSheet?.(sheet.id)}
                    // ПКМ по карточке — меню видимости (GM) или ничего; НЕ
                    // всплывать до трека, где ПКМ циклит размер аватарок —
                    // иначе открытие меню одновременно меняло бы размер.
                    onContextMenu={(e) => e.stopPropagation()}
                    style={{ '--accent-ring': accentColor, width: size.width, height: size.height } as React.CSSProperties}
                    onDragStart={(e) => {
                      dragId.current = sheet.id;
                      e.dataTransfer.effectAllowed = 'move';
                      setTimeout(() => setDragOverId(null), 0);
                    }}
                    onDragEnd={() => {
                      dragId.current = null;
                      setDragOverId(null);
                    }}
                    onDragOver={(e) => {
                      e.preventDefault();
                      e.dataTransfer.dropEffect = 'move';
                      if (sheet.id !== dragId.current) setDragOverId(sheet.id);
                    }}
                    onDragLeave={() => setDragOverId(null)}
                    onDrop={(e) => {
                      e.preventDefault();
                      const from = dragId.current;
                      const to = sheet.id;
                      if (!from || from === to) return;
                      setOrder((prev) => {
                        const next = [...prev];
                        const fi = next.indexOf(from);
                        const ti = next.indexOf(to);
                        next.splice(fi, 1);
                        next.splice(ti, 0, from);
                        persistOrder(next);
                        return next;
                      });
                      dragId.current = null;
                      setDragOverId(null);
                    }}
                  >
                    <div className="character-portraits__card">
                      <div className="character-portraits__portrait-area">
                        {sheet.portrait ? (
                          <PortraitMedia
                            className="character-portraits__img"
                            src={sheet.portrait}
                            alt={sheet.name}
                          />
                        ) : (
                          <span className="character-portraits__initials">
                            {getInitials(sheet.name)}
                          </span>
                        )}
                      </div>

                      {isOnline && (
                        <span className="character-portraits__status online" />
                      )}

                      {/* GM: бейдж «скрыт от игроков» — полупрозрачность сама
                          по себе читается как «оффлайн», нужен явный маркер.
                          hidden — тот же бейдж в «тревожном» варианте. */}
                      {isGm && effVisibility !== 'always' && (
                        <span
                          className={`character-portraits__visibility-badge${effVisibility === 'hidden' ? ' character-portraits__visibility-badge--hidden' : ''}`}
                          title={effVisibility === 'hidden'
                            ? t('characterPortraits.visibility.hidden')
                            : t('characterPortraits.hiddenFromPlayers')}
                        >
                          <EyeOff size={12} />
                        </span>
                      )}

                      {/* GM: отдельный бейдж «ЛИСТ скрыт» (sheet.visible=false,
                          hide в списке персонажей) — иначе рассинхрон «карточка
                          видна, а листа для игроков нет» невозможно заметить. */}
                      {isGm && sheet.visible === false && (
                        <span
                          className="character-portraits__visibility-badge character-portraits__visibility-badge--sheet"
                          title={t('characterPortraits.sheetHiddenBadge')}
                        >
                          <FileX size={12} />
                        </span>
                      )}

                      <div className="character-portraits__footer">
                        <span className="character-portraits__name">
                          {sheet.name}
                        </span>
                        {showHpBar && (
                          <div className="character-portraits__hp-bar">
                            <div
                              className={`character-portraits__hp-fill ${getHpFillClass(hp, maxHp)}`}
                              style={{ width: `${hpPct}%` }}
                            />
                          </div>
                        )}
                      </div>

                      {isSelf && (
                        <div
                          className="character-portraits__color-btn"
                          style={{ background: accentColor }}
                          title={t('characterPortraits.changeColor')}
                          onClick={(e) => e.stopPropagation()}
                        >
                          <input
                            type="color"
                            className="character-portraits__color-input"
                            value={selfColor}
                            onChange={(e) => handleColorChange(e.target.value)}
                            onClick={(e) => e.stopPropagation()}
                          />
                        </div>
                      )}
                    </div>
                  </button>
            );

            // ПКМ-меню на карточке доступно ВСЕМ: карточки покрывают почти
            // весь трек, поэтому смена размера аватарок обязана жить и здесь
            // (ПКМ по пустому месту трека остаётся). Radio-пункты показывают
            // ТЕКУЩЕЕ значение галкой — статус видимости/размера виден сразу.
            const trigger = (
              <ContextMenu>
                <ContextMenuTrigger asChild>{portraitButton}</ContextMenuTrigger>
                <ContextMenuContent
                  onContextMenu={(e: React.MouseEvent) => e.stopPropagation()}
                >
                  {isGm && (
                    <>
                      <ContextMenuLabel>{t('characterPortraits.visibilityLabel')}</ContextMenuLabel>
                      <ContextMenuRadioGroup
                        value={effVisibility}
                        onValueChange={(v: string) => setVisibility(sheet.id, v as PortraitVisibility)}
                      >
                        <ContextMenuRadioItem value="always">
                          {t('characterPortraits.visibility.always')}
                        </ContextMenuRadioItem>
                        <ContextMenuRadioItem value="gm-only">
                          {t('characterPortraits.visibility.gmOnly')}
                        </ContextMenuRadioItem>
                        <ContextMenuRadioItem value="hidden">
                          {t('characterPortraits.visibility.hidden')}
                        </ContextMenuRadioItem>
                      </ContextMenuRadioGroup>
                      <ContextMenuSeparator />
                      {/* Консолидация видимости (2026-07-08): видимость ЛИСТА
                          (hide/show из списка персонажей) управляется и отсюда —
                          раньше две похожие ручки жили в разных местах и
                          путались. Карточка выше — про присутствие на сцене,
                          этот пункт — существует ли лист для игроков вообще. */}
                      <ContextMenuLabel>{t('characterPortraits.sheetLabel')}</ContextMenuLabel>
                      <ContextMenuCheckboxItem
                        checked={sheet.visible !== false}
                        onCheckedChange={(checked: boolean) => {
                          try {
                            mp.send({ type: 'sheet:set-visible', sheetId: sheet.id, visible: checked } as any);
                          } catch {}
                        }}
                      >
                        {t('characterPortraits.sheetVisibleToPlayers')}
                      </ContextMenuCheckboxItem>
                      <ContextMenuSeparator />
                    </>
                  )}
                  <ContextMenuLabel>{t('characterPortraits.sizeLabel')}</ContextMenuLabel>
                  <ContextMenuRadioGroup
                    value={avatarSize ?? 'medium'}
                    onValueChange={(v: string) => onAvatarSizeChange?.(v as AvatarSize)}
                  >
                    <ContextMenuRadioItem value="small">
                      {t('characterPortraits.size.small')}
                    </ContextMenuRadioItem>
                    <ContextMenuRadioItem value="medium">
                      {t('characterPortraits.size.medium')}
                    </ContextMenuRadioItem>
                    <ContextMenuRadioItem value="large">
                      {t('characterPortraits.size.large')}
                    </ContextMenuRadioItem>
                  </ContextMenuRadioGroup>
                </ContextMenuContent>
              </ContextMenu>
            );

            return (
              <Tooltip key={sheet.id}>
                <TooltipTrigger asChild>{trigger}</TooltipTrigger>
                <TooltipContent side="top" sideOffset={8}>
                  <div className="character-portraits__tooltip">
                    <span className="character-portraits__tooltip-name">
                      {sheet.name}
                    </span>
                    {sheet.ownerName && (
                      <span className="character-portraits__tooltip-owner">
                        {sheet.ownerName} {isOnline ? `● ${t('characterPortraits.online')}` : `○ ${t('characterPortraits.offline')}`}
                      </span>
                    )}
                    {showHpBar && (
                      <span className="character-portraits__tooltip-hp">
                        HP: {hp} / {maxHp}
                      </span>
                    )}
                    {isActor && (
                      <span className="character-portraits__tooltip-actor">
                        ★ {t('characterPortraits.actingAs')}
                      </span>
                    )}
                    {isGm && effVisibility !== 'always' && (
                      <span className="character-portraits__tooltip-visibility">
                        {effVisibility === 'gm-only'
                          ? t('characterPortraits.hiddenFromPlayers')
                          : t('characterPortraits.visibility.hidden')}
                      </span>
                    )}
                  </div>
                </TooltipContent>
              </Tooltip>
            );
          })}
        </div>
      </div>
    </TooltipProvider>
  );
});
export default CharacterPortraits;
